import { type RefCallback, type RefObject, useCallback, useRef } from 'react'

const DEFAULT_HIGHLIGHT_DURATION_MS = 5000

interface UseHighlightingOptions {
  commentId: number
  highlightedCommentId: number | null | undefined
  onHighlightComplete?: (commentId: number) => void
  duration?: number
}

interface UseHighlightingResult {
  elementRef: RefObject<HTMLDivElement | null>
  isHighlighted: boolean
  ref: RefCallback<HTMLDivElement>
}

export function useHighlighting({
  commentId,
  highlightedCommentId,
  onHighlightComplete,
  duration = DEFAULT_HIGHLIGHT_DURATION_MS,
}: UseHighlightingOptions): UseHighlightingResult {
  const elementRef = useRef<HTMLDivElement | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const isHighlighted = highlightedCommentId === commentId
  const ref = useCallback<RefCallback<HTMLDivElement>>(
    element => {
      elementRef.current?.style.removeProperty('--comment-composer-height')
      elementRef.current = element
      clearTimeout(timerRef.current)
      resizeObserverRef.current?.disconnect()
      if (element === null || !isHighlighted) return
      const composer = element
        .closest('[data-comments-list]')
        ?.querySelector<HTMLElement>('[data-comment-composer]')
      resizeObserverRef.current = new ResizeObserver(() => {
        element.style.setProperty(
          '--comment-composer-height',
          `${composer?.getBoundingClientRect().height ?? 0}px`,
        )
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
      resizeObserverRef.current.observe(element)
      if (composer) resizeObserverRef.current.observe(composer)
      timerRef.current = setTimeout(() => {
        resizeObserverRef.current?.disconnect()
        onHighlightComplete?.(commentId)
      }, duration)
    },
    [commentId, duration, isHighlighted, onHighlightComplete],
  )

  return { elementRef, isHighlighted, ref }
}
